/**
 * -----------------------------------------------------------------------------
 * Drivers Listing Page
 * -----------------------------------------------------------------------------
 * This page is used to list all the drivers of the organization.
 */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationDriversQuery } from "../../../api/network/adminApiServices";
import {
  setListingQueryParams,
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import { FilterType } from "../../../api/types/Admin";
import { TLegacyDriver } from "../../../api/types/Driver";
import { TSelectboxOption } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useListingFilters } from "../../../hooks/listingFilter";
import { routeUrls } from "../../../navigation/routeUrls";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import { calculateGregorianDate } from "../../../utils/dateTime";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import { TAllColumn } from "../vehicles/vehicles";
import AdminsDriversCreateNew from "./createNewDriver";

const columnOrderingFields: Record<string, string> = {
  "Driver ID": "driverid",
  "Driver Name": "description",
  "Employee ID": "badgeid",
  "Card ID": "cardid",
  "License Number": "licensenumber",
  "License Type": "licensetype",
  "License Expiry": "licenseexpire",
  "Hazmat Certified": "hazmatcert",
  Active: "isactive",
};

// const all_columns = [
//   "Driver ID",
//   "Driver Name",
//   "Employee ID",
//   "Card ID",
//   "License Number",
//   "License Type",
//   "License Expiry",
//   "Hazmat Certified",
//   "Active",
// ]

const ScreenDashboardAdminDrivers = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", { keyPrefix: "admins.drivers" });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { drivers: orgDriversQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  const { getUserACLAccessLevel } = useACL();
  const thisOrgDriverGroupAllowed = useLoggedInUserData("hasdrivers");

  const [all_columns, setAllColumns] = React.useState<TAllColumn[]>([]);
  useEffect(() => {
    setAllColumns([
      { name: "Driver ID", acl: "" },
      { name: "Driver Name", acl: "" },
      {
        name: "Employee ID",
        acl: ACL_NAMES.ACL_ADMIN_DRIVER_BADGE,
        accountLevelAccess: thisOrgDriverGroupAllowed === "1",
      },
      {
        name: "Card ID",
        acl: ACL_NAMES.ACL_ADMIN_DRIVER_CARD,
        accountLevelAccess: thisOrgDriverGroupAllowed === "1",
      },
      {
        name: "License Number",
        acl: ACL_NAMES.ACL_ADMIN_DRIVER_LICENSE,
        accountLevelAccess: thisOrgDriverGroupAllowed === "1",
      },
      {
        name: "License Type",
        acl: ACL_NAMES.ACL_ADMIN_DRIVER_LICENSE,
        accountLevelAccess: thisOrgDriverGroupAllowed === "1",
      },
      {
        name: "License Expiry",
        acl: ACL_NAMES.ACL_ADMIN_DRIVER_LICENSE,
        accountLevelAccess: thisOrgDriverGroupAllowed === "1",
      },
      { name: "Hazmat Certified", acl: "" },
      {
        name: "Active",
        acl: ACL_NAMES.ACL_ADMIN_DRIVER_ACTIVE,
        accountLevelAccess: thisOrgDriverGroupAllowed === "1",
      },
    ]);
  }, [thisOrgDriverGroupAllowed]);

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(localStorageKeys.columns.Drivers);
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns
      .filter((column) => {
        return (
          (column.acl
            ? getUserACLAccessLevel(column.acl) > ROLE_ACCESS_LEVELS.NONE
            : true) &&
          (column?.accountLevelAccess !== undefined
            ? column?.accountLevelAccess
            : true)
        );
      })
      .map((colItem, _) => {
        return {
          name: colItem.name,
          show: !!savedColumns ? savedColumns.includes(colItem.name) : true,
        };
      })
  );
  useEffect(() => {
    setColumns(
      // all_columns
      //   .filter((column) =>
      //     column.acl
      //       ? getUserACLAccessLevel(column.acl) > ROLE_ACCESS_LEVELS.NONE
      //       : true
      //   )
      //   .map((colItem, _) => {
      //     return {
      //       name: colItem.name,
      //       show: !!savedColumns ? savedColumns.includes(colItem.name) : true,
      //     };
      //   })
      all_columns
        .filter((column) => {
          return (
            (column.acl
              ? getUserACLAccessLevel(column.acl) > ROLE_ACCESS_LEVELS.NONE
              : true) &&
            (column?.accountLevelAccess !== undefined
              ? column?.accountLevelAccess
              : true)
          );
        })
        .map((colItem, _) => {
          return {
            name: colItem.name,
            show: !!savedColumns ? savedColumns.includes(colItem.name) : true,
          };
        })
    );
  }, [savedColumns, all_columns]);

  // filter mechainism
  const { filters } = useListingFilters();
  const [activeFilterSlug, setActiveFilterSlug] =
    React.useState<FilterType>("active");

  // updating query params on filter change
  useEffect(() => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        drivers: {
          ...orgDriversQueryParams,
          page: 1,
          isactive:
            activeFilterSlug === "both"
              ? undefined
              : activeFilterSlug === "active"
                ? 1
                : 0,
        },
      })
    ); // TODO: [P] Check with BE for both case what should be number
  }, [activeFilterSlug]);

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        drivers: { ...orgDriversQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        drivers: {
          ...orgDriversQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  // fetching driver data
  const {
    data: dataOrgDrivers,
    isFetching: isFetchingOrgDrivers,
    error,
  } = useOrganizationDriversQuery({
    ...orgDriversQueryParams,
    // 'search_fields' intentionally not added here as default search fields are defined in the API service
  });
  const { count, next, previous, results } = dataOrgDrivers || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgDrivers]);

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as TLegacyDriver[])).map(
        (item: TLegacyDriver, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.adminChildren.drivers}/${item.driverid}`,
          cellData: [
            item?.driverid, // "Driver Id",
            item?.displayname || item?.description, // "Driver Name",
            item?.badgeid ?? "-", // "Employee ID",
            item?.cardid ?? "-", // "Card Id",
            item?.licensenumber ?? "-", // "License Number",
            item?.licensetype ?? "-", // "License Type",
            // Change license expiry to date using gregorian calendar
            item?.licenseexpire
              ? calculateGregorianDate(item.licenseexpire)
              : "-", // "License Expiry",
            item?.hazmatcert ? (
              <span className="text-field-success">{tMain("yes")}</span>
            ) : (
              <span className="text-field-error-dark">{tMain("no")}</span>
            ), // "Hazmat Certified",
            item?.isactive === 1 ? (
              <span className="text-field-success">{tMain("yes")}</span>
            ) : item?.isactive === 0 ? (
              <span className="text-field-error-dark">{tMain("no")}</span>
            ) : (
              "-"
            ), // "Active"
          ].filter((_, index) => columns[index]?.show),
          isActive: item?.isactive === 1,
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          {...(thisOrgDriverGroupAllowed === "1" &&
            getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER) >=
              ROLE_ACCESS_LEVELS.ALL && {
              buttonText: t("add_new"),
              buttonCallback: () => {
                dispatch(
                  setModalsData({ ...modalsState, showCreateDriver: true })
                );
              },
            })}
        />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgDriversQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgDriversQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgDriversQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
          filters={filters}
          activeFilterSlug={activeFilterSlug}
          handleFilterChange={(slug) => setActiveFilterSlug(slug)}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.Drivers}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton disabled={isFetchingOrgDrivers} />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingOrgDrivers}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgDriversQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgDrivers && (
            <Pagination
              pageSize={orgDriversQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    drivers: {
                      ...orgDriversQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count ? Math.ceil(count / orgDriversQueryParams.page_size) : 1
              }
              forcePage={orgDriversQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    drivers: {
                      ...orgDriversQueryParams,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={orgDriversQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
      <AdminsDriversCreateNew />
    </>
  );
};

export default ScreenDashboardAdminDrivers;
